import type { TrustedDeviceChallenge, TrustedDeviceProof } from "../types";
import {
  BrowserTrustedDeviceError,
  toStorageFailure,
} from "./browser-trusted-device-error.ts";

type StoredBrowserInstallation = {
  installationId: string;
  privateKey: CryptoKey;
  publicKey: CryptoKey;
};

const databaseName = "feather-trusted-device";
const installationKey = "browser";
const installationStore = "installations";
const minimumInstallationIdLength = 16;

/**
 * Controls cancellation and enrollment of trusted-device proof preparation.
 *
 * `allowCreate: false` only uses an already stored key — the login ladder
 * passes it when this browser already enrolled through a fallback, so a
 * strong key is never minted on top of it (which would cost another device
 * slot).
 *
 * @example
 * const controller = new AbortController();
 * const proof = createBrowserTrustedDeviceProof(requestChallenge, {
 *   signal: controller.signal,
 * });
 */
export type BrowserTrustedDeviceProofOptions = {
  signal?: AbortSignal;
  allowCreate?: boolean;
};

function createAbortError() {
  const error = new Error("Trusted-device preparation was abandoned");

  error.name = "AbortError";

  return error;
}

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

function hasInstallationStore(database: IDBDatabase) {
  const names = database.objectStoreNames as DOMStringList | undefined;

  // Test doubles and very old engines may not expose the list; assume present.
  if (!names || typeof names.contains !== "function") return true;

  return names.contains(installationStore);
}

function openDatabaseRequest(version?: number) {
  try {
    return version === undefined
      ? indexedDB.open(databaseName)
      : indexedDB.open(databaseName, version);
  } catch (error) {
    // Some private modes throw synchronously (SecurityError/InvalidStateError).
    throw toStorageFailure(error, "Trusted-device storage open failed");
  }
}

/**
 * Open the installation database at whatever version it already has.
 *
 * Opening without a fixed version avoids a permanent `VersionError` if the
 * database was ever bumped, and a database that exists without its store
 * (an interrupted first upgrade) is repaired by one version bump.
 */
function openTrustedDeviceDatabase(
  signal?: AbortSignal,
  version?: number,
): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());

      return;
    }

    let request: IDBOpenDBRequest;

    try {
      request = openDatabaseRequest(version);
    } catch (error) {
      reject(error);

      return;
    }

    let wasBlocked = false;
    let isSettled = false;

    const onAbort = () => {
      if (isSettled) return;

      isSettled = true;

      reject(createAbortError());
    };

    signal?.addEventListener("abort", onAbort, { once: true });

    request.onerror = () => {
      signal?.removeEventListener("abort", onAbort);

      if (isSettled) return;

      isSettled = true;

      reject(
        toStorageFailure(request.error, "Trusted-device storage open failed"),
      );
    };
    request.onblocked = () => {
      wasBlocked = true;
      signal?.removeEventListener("abort", onAbort);

      if (isSettled) return;

      isSettled = true;

      reject(
        new BrowserTrustedDeviceError(
          "storageBlocked",
          "Trusted-device storage is blocked by another tab",
        ),
      );
    };
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(installationStore)) {
        request.result.createObjectStore(installationStore);
      }
    };
    request.onsuccess = () => {
      signal?.removeEventListener("abort", onAbort);

      const database = request.result;

      // A late open that nobody awaits any more must not leak its connection,
      // or it would block the next attempt's open and version changes.
      if (wasBlocked || isSettled) {
        database.close();

        return;
      }

      isSettled = true;

      // Never hold up another tab's upgrade with this short-lived connection.
      database.onversionchange = () => database.close();

      if (hasInstallationStore(database)) {
        resolve(database);

        return;
      }

      const repairVersion = Number(database.version || 1) + 1;

      database.close();

      if (version !== undefined) {
        reject(
          new BrowserTrustedDeviceError(
            "storageUnavailable",
            "Trusted-device storage is missing its store",
          ),
        );

        return;
      }

      openTrustedDeviceDatabase(signal, repairVersion).then(resolve, reject);
    };
  });
}

/**
 * Run one storage operation, retrying once on a generic storage failure.
 *
 * Safari can drop its IndexedDB server connection ("Connection to Indexed
 * Database server lost") or abort a transaction without a reason; a fresh
 * connection usually succeeds. Quota, clone, and blocked failures are not
 * retried because a second attempt cannot change their outcome.
 */
async function withStorageRetry<TResult>(
  operation: () => Promise<TResult>,
  signal?: AbortSignal,
): Promise<TResult> {
  try {
    return await operation();
  } catch (error) {
    if (
      isAbortError(error) ||
      !(error instanceof BrowserTrustedDeviceError) ||
      error.kind !== "storageUnavailable"
    ) {
      throw error;
    }

    throwIfAborted(signal);

    return operation();
  }
}

function isStoredInstallation(
  value: unknown,
): value is StoredBrowserInstallation {
  if (!value || typeof value !== "object") return false;

  const record = value as Partial<StoredBrowserInstallation>;

  return (
    typeof record.installationId === "string" &&
    record.installationId.length >= minimumInstallationIdLength &&
    Boolean(record.privateKey) &&
    typeof record.privateKey === "object" &&
    Boolean(record.publicKey) &&
    typeof record.publicKey === "object"
  );
}

async function readInstallationOnce(signal?: AbortSignal) {
  const database = await openTrustedDeviceDatabase(signal);

  try {
    return await new Promise<StoredBrowserInstallation | undefined>(
      (resolve, reject) => {
        let installation: StoredBrowserInstallation | undefined;

        try {
          const transaction = database.transaction(
            installationStore,
            "readonly",
          );
          const request = transaction
            .objectStore(installationStore)
            .get(installationKey);

          request.onerror = () =>
            reject(toStorageFailure(request.error, "Trusted-device read failed"));
          request.onsuccess = () => {
            installation = isStoredInstallation(request.result)
              ? request.result
              : undefined;
          };
          transaction.oncomplete = () => resolve(installation);
          transaction.onerror = () =>
            reject(
              toStorageFailure(transaction.error, "Trusted-device read failed"),
            );
          transaction.onabort = () =>
            reject(
              toStorageFailure(
                transaction.error,
                "Trusted-device read aborted",
              ),
            );
        } catch (error) {
          reject(toStorageFailure(error, "Trusted-device read failed"));
        }
      },
    );
  } finally {
    database.close();
  }
}

/**
 * Store a new installation unless another tab stored one first.
 *
 * The read and the write share one `readwrite` transaction, so two tabs that
 * log in at the same moment converge on one key (and one device slot) instead
 * of the last writer silently replacing the key the first tab enrolled.
 */
async function storeInstallationIfAbsentOnce(
  installation: StoredBrowserInstallation,
  replace: boolean,
  signal?: AbortSignal,
) {
  const database = await openTrustedDeviceDatabase(signal);

  try {
    return await new Promise<StoredBrowserInstallation>((resolve, reject) => {
      let stored = installation;
      let failure: BrowserTrustedDeviceError | undefined;

      try {
        const transaction = database.transaction(installationStore, "readwrite");
        const store = transaction.objectStore(installationStore);

        const write = () => {
          try {
            store.put(installation, installationKey);
          } catch (error) {
            // DataCloneError is thrown synchronously by put().
            failure = toStorageFailure(error, "Trusted-device write failed");
            transaction.abort();
          }
        };

        transaction.onerror = () =>
          reject(
            failure ||
              toStorageFailure(transaction.error, "Trusted-device write failed"),
          );
        transaction.onabort = () =>
          reject(
            failure ||
              toStorageFailure(transaction.error, "Trusted-device write aborted"),
          );
        transaction.oncomplete = () => resolve(stored);

        if (replace) {
          write();

          return;
        }

        const existing = store.get(installationKey);

        existing.onsuccess = () => {
          if (isStoredInstallation(existing.result)) {
            stored = existing.result;

            return;
          }

          write();
        };
      } catch (error) {
        reject(toStorageFailure(error, "Trusted-device write failed"));
      }
    });
  } finally {
    database.close();
  }
}

function requestPersistentBrowserStorage() {
  try {
    const persistence = navigator.storage?.persist?.();

    void persistence?.catch(() => undefined);
  } catch {
    // Storage persistence is optional and must never block a login proof.
  }
}

function createInstallationId() {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return Array.from(crypto.getRandomValues(new Uint8Array(24)))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function generateInstallation(): Promise<StoredBrowserInstallation> {
  let keyPair: CryptoKeyPair;

  try {
    keyPair = await crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["sign", "verify"],
    );
  } catch (error) {
    throw new BrowserTrustedDeviceError(
      "keyUnsupported",
      "ECDSA P-256 key generation is unavailable",
      { cause: error },
    );
  }

  return {
    installationId: createInstallationId(),
    privateKey: keyPair.privateKey,
    publicKey: keyPair.publicKey,
  };
}

async function getOrCreateInstallation(
  signal: AbortSignal | undefined,
  allowCreate: boolean,
) {
  const existing = await withStorageRetry(
    () => readInstallationOnce(signal),
    signal,
  );

  throwIfAborted(signal);

  if (existing) {
    requestPersistentBrowserStorage();

    return { installation: existing, created: false };
  }

  if (!allowCreate) {
    throw new BrowserTrustedDeviceError(
      "notEnrolled",
      "No stored trusted-device key and enrollment is deferred to a fallback",
    );
  }

  const generated = await generateInstallation();

  // An abandoned attempt must not persist a key the caller will never use.
  throwIfAborted(signal);

  const installation = await withStorageRetry(
    () => storeInstallationIfAbsentOnce(generated, false, signal),
    signal,
  );

  requestPersistentBrowserStorage();

  return { installation, created: installation === generated };
}

async function replaceInstallation(signal?: AbortSignal) {
  const generated = await generateInstallation();

  throwIfAborted(signal);

  return withStorageRetry(
    () => storeInstallationIfAbsentOnce(generated, true, signal),
    signal,
  );
}

function bytesToBase64(bytes: ArrayBuffer) {
  let binary = "";

  for (const byte of new Uint8Array(bytes)) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
}

/**
 * Request a challenge for the installation and sign it.
 *
 * Challenge failures are re-thrown untouched (the caller attributes them to
 * the network round-trip); only signing/export failures become
 * `signingFailed`.
 */
async function signChallenge(
  installation: StoredBrowserInstallation,
  requestChallenge: (installationId: string) => Promise<TrustedDeviceChallenge>,
  signal?: AbortSignal,
): Promise<TrustedDeviceProof> {
  const challenge = await requestChallenge(installation.installationId);

  throwIfAborted(signal);

  let signature: ArrayBuffer;
  let publicKey: ArrayBuffer;

  try {
    signature = await crypto.subtle.sign(
      { hash: "SHA-256", name: "ECDSA" },
      installation.privateKey,
      new TextEncoder().encode(challenge.challenge),
    );
    publicKey = await crypto.subtle.exportKey("spki", installation.publicKey);
  } catch (error) {
    throw new BrowserTrustedDeviceError(
      "signingFailed",
      "The stored trusted-device key could not sign the challenge",
      { cause: error },
    );
  }

  return {
    challengeToken: challenge.challengeToken,
    installationId: installation.installationId,
    keyAlgorithm: "ES256",
    name: navigator.platform || "Web browser",
    platform: "web",
    publicKey: bytesToBase64(publicKey),
    signature: bytesToBase64(signature),
    verificationMethod: "webCrypto",
  };
}

/**
 * Create a browser installation proof without exporting its private key.
 *
 * @remarks
 * A stored key that can no longer sign (corrupted record, engine bug after a
 * browser update) is replaced once by a fresh key and re-registered, instead
 * of locking the student out on every attempt. The replacement is a new
 * installation, so it is enrolled as a new device on the backend.
 *
 * @example
 * const proof = await createBrowserTrustedDeviceProof(requestChallenge, {
 *   signal: controller.signal,
 * });
 */
export async function createBrowserTrustedDeviceProof(
  requestChallenge: (installationId: string) => Promise<TrustedDeviceChallenge>,
  options: BrowserTrustedDeviceProofOptions = {},
): Promise<TrustedDeviceProof> {
  const { signal, allowCreate = true } = options;
  const { installation, created } = await getOrCreateInstallation(
    signal,
    allowCreate,
  );

  throwIfAborted(signal);

  try {
    return await signChallenge(installation, requestChallenge, signal);
  } catch (error) {
    if (
      created ||
      !(error instanceof BrowserTrustedDeviceError) ||
      error.kind !== "signingFailed"
    ) {
      throw error;
    }
  }

  throwIfAborted(signal);

  const replacement = await replaceInstallation(signal);

  throwIfAborted(signal);

  return signChallenge(replacement, requestChallenge, signal);
}
